import React, { createContext, ReactNode, useReducer, useContext } from 'react';

import { IEventEnrollment, IEventRound } from '../../../../store/player/events/store';

import { IObjectById } from '../../../../contrib/Utils/getObjectByIdFromArray';
import { GlobalContext } from '../../../../store';

export type TSelectedRound = IEventRound | undefined;

type IActions =
  | { type: 'selectEvent' }
  | { type: 'selectFlightId'; value: number }
  | { type: 'setEnrollments'; value: IObjectById<IEventEnrollment> }
  | { type: 'setOpenModal'; value: boolean }
  | { type: 'setSelectedRound'; value: TSelectedRound }
  | { type: 'toggleOpenModal' };

interface IEventLeaderboardsState {
  enrollments: IObjectById<IEventEnrollment>;
  openModal: boolean;
  selectedFlightId: number;
  selectedRound: TSelectedRound;
  isEventSelected: boolean;
}

const initialState: IEventLeaderboardsState = {
  enrollments: {},
  isEventSelected: false,
  openModal: false,
  selectedFlightId: -1,
  selectedRound: undefined
};

interface IDispatch {
  dispatch: (action: IActions) => void;
  actions: IBoundActionCreators;
}

const EventLeaderboardsStateContext = createContext<IEventLeaderboardsState | undefined>(undefined);
const EventLeaderboardsDispatchContext = createContext<IDispatch | undefined>(undefined);

const EventLeaderboardsReducer = (
  state: IEventLeaderboardsState,
  action: IActions
): IEventLeaderboardsState => {
  switch (action.type) {
    case 'toggleOpenModal': {
      return {
        ...state,
        openModal: !state.openModal
      };
    }
    case 'setOpenModal': {
      return {
        ...state,
        openModal: action.value
      };
    }
    case 'setSelectedRound': {
      return {
        ...state,
        isEventSelected: false,
        selectedRound: action.value
      };
    }
    case 'selectEvent': {
      return {
        ...state,
        isEventSelected: true,
        selectedRound: undefined
      };
    }
    case 'setEnrollments': {
      return {
        ...state,
        enrollments: action.value
      };
    }
    case 'selectFlightId': {
      return {
        ...state,
        selectedFlightId: action.value
      };
    }
  }
};

interface CountProviderProps {
  children: ReactNode;
}

interface IBoundActionCreators {
  setCombinations: any;
}

const EventLeaderboardsProvider = ({ children }: CountProviderProps) => {
  const [state, dispatch] = useReducer(EventLeaderboardsReducer, initialState as never);

  const {} = useContext(GlobalContext);

  const actions: IBoundActionCreators = {
    setCombinations: null
  };

  return (
    <EventLeaderboardsStateContext.Provider value={state}>
      <EventLeaderboardsDispatchContext.Provider value={{ dispatch, actions }}>
        {children}
      </EventLeaderboardsDispatchContext.Provider>
    </EventLeaderboardsStateContext.Provider>
  );
};

const useEventLeaderboardsState = () => {
  const context = useContext(EventLeaderboardsStateContext);
  if (context === undefined) {
    throw new Error('useEventLeaderboardsState must be used within a EventLeaderboardsProvider');
  }
  return context;
};

const useEventLeaderboardsDispatch = () => {
  const context = useContext(EventLeaderboardsDispatchContext);
  if (context === undefined) {
    throw new Error('useEventLeaderboardsDispatch must be used within a EventLeaderboardsProvider');
  }
  return context;
};

export { EventLeaderboardsProvider, useEventLeaderboardsState, useEventLeaderboardsDispatch };
